/**
 * Device link — the browser client end of the device-room relay
 * (`crates/rpc/src/device_room.rs` `DeviceLink` + `LinkCache`).
 *
 * One WebSocket to `wss://edge/device/{deviceId}/ws?role=client&connId=…&token=…`,
 * carrying binary device frames whose `rpc` payloads are NDJSON envelopes. The
 * link owns:
 *
 * - **Liveness.** Browsers cannot send WebSocket control pings, so the Rust
 *   client's `echo` frame is the only end-to-end signal: the DO's auto-pong
 *   proves the client↔edge leg, and a host echo proves the edge↔host leg. The
 *   link sends one echo immediately (fast feature detection) and every
 *   `echoIntervalMs`, and treats silence past `echoDeadlineMs` as a zombie path
 *   worth redialing.
 * - **Relay control.** `" relay"` frames carry `host_offline` / `host_closed`.
 *   Those are not errors to surface as failures — they mean "the workstation is
 *   asleep" — so the link goes to `host_offline` and keeps retrying with backoff.
 * - **Reconnect.** The protocol has no resume token, so every reconnect is a
 *   fresh `connId` and the app must reissue its watches; `onReady` fires on each
 *   successful (re)connect for exactly that.
 */

import {
  DeviceFrameError,
  ECHO_KIND,
  HOST_CLOSED,
  HOST_OFFLINE,
  NUDGE_KIND,
  RELAY_KIND,
  RPC_KIND,
  decodeDeviceFrame,
  deviceRoomWsUrl,
  encodeDeviceFrame,
  relayErrorCode
} from "./device-frame";

/** The subset of `WebSocket` the link uses, so tests can supply a fake. */
export interface SocketLike {
  binaryType: string;
  readonly readyState: number;
  send(data: ArrayBufferView | ArrayBuffer): void;
  close(code?: number, reason?: string): void;
  onopen: ((event: unknown) => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onclose: ((event: { code?: number; reason?: string }) => void) | null;
  onerror: ((event: unknown) => void) | null;
}

export type SocketFactory = (url: string) => SocketLike;

export const browserSocketFactory: SocketFactory = (url) => {
  const socket = new WebSocket(url);
  // Required: without it Safari hands back a Blob and the frame codec has
  // nothing to read.
  socket.binaryType = "arraybuffer";
  return socket as unknown as SocketLike;
};

export type LinkState =
  | "idle"
  | "connecting"
  | "online"
  | "reconnecting"
  /** The relay has no live host socket: the device is asleep or offline. */
  | "host_offline"
  /** Auth was rejected (401/403 on the upgrade, or the token expired). */
  | "unauthorized"
  | "closed";

export interface LinkTimers {
  setTimeout: typeof setTimeout;
  clearTimeout: typeof clearTimeout;
  setInterval: typeof setInterval;
  clearInterval: typeof clearInterval;
  now: () => number;
  random: () => number;
  uuid: () => string;
}

export const defaultTimers: LinkTimers = {
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (handle) => clearTimeout(handle),
  setInterval: (fn, ms) => setInterval(fn, ms),
  clearInterval: (handle) => clearInterval(handle),
  now: () => Date.now(),
  random: () => Math.random(),
  uuid: () =>
    globalThis.crypto?.randomUUID?.() ??
    `c-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`
};

export interface DeviceLinkOptions {
  edgeUrl: string;
  deviceId: string;
  /**
   * Resolves a *short-lived access token* per dial. Called on every (re)connect
   * so a refreshed token is picked up; returning undefined aborts the dial.
   */
  tokenProvider: () => Promise<string | undefined>;
  socketFactory?: SocketFactory;
  timers?: Partial<LinkTimers>;
  /** Echo cadence; Rust uses 10s. */
  echoIntervalMs?: number;
  /** Echo silence deadline once the host has echoed; Rust uses 20s. */
  echoDeadlineMs?: number;
  /** Transport silence deadline before redialing (Rust's 25s lease). */
  silenceDeadlineMs?: number;
  backoffBaseMs?: number;
  backoffMaxMs?: number;
  onState?: (state: LinkState, detail?: string) => void;
  /** Inbound RPC text (one or more NDJSON envelopes). */
  onRpcText?: (text: string) => void;
  /** Fired after each successful connect — resubscribe every watch here. */
  onReady?: () => void;
  /** Diagnostics; never thrown. */
  onWarning?: (message: string) => void;
}

/** Rust `PING_INTERVAL` / `ECHO_DEADLINE` / `SILENCE_LEASE` defaults. */
const ECHO_INTERVAL_MS = 10_000;
const ECHO_DEADLINE_MS = 20_000;
const SILENCE_DEADLINE_MS = 25_000;
const BACKOFF_BASE_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;

export class DeviceLink {
  private readonly timers: LinkTimers;
  private readonly socketFactory: SocketFactory;
  private readonly echoIntervalMs: number;
  private readonly echoDeadlineMs: number;
  private readonly silenceDeadlineMs: number;
  private readonly backoffBaseMs: number;
  private readonly backoffMaxMs: number;

  private socket: SocketLike | undefined;
  private state: LinkState = "idle";
  /** Extra observers for [`waitForOnline`], alongside the single `onState`. */
  private readonly stateListeners = new Set<(state: LinkState, detail?: string) => void>();
  private connId: string | undefined;
  private echoTimer: ReturnType<typeof setInterval> | undefined;
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  private attempt = 0;
  private lastEchoAt = 0;
  private lastInboundAt = 0;
  private echoSeen = false;
  private userClosed = false;
  /** Guards against a stale socket's events landing after a redial. */
  private generation = 0;

  constructor(private readonly options: DeviceLinkOptions) {
    this.timers = { ...defaultTimers, ...options.timers };
    this.socketFactory = options.socketFactory ?? browserSocketFactory;
    this.echoIntervalMs = options.echoIntervalMs ?? ECHO_INTERVAL_MS;
    this.echoDeadlineMs = options.echoDeadlineMs ?? ECHO_DEADLINE_MS;
    this.silenceDeadlineMs = options.silenceDeadlineMs ?? SILENCE_DEADLINE_MS;
    this.backoffBaseMs = options.backoffBaseMs ?? BACKOFF_BASE_MS;
    this.backoffMaxMs = options.backoffMaxMs ?? BACKOFF_MAX_MS;
  }

  get currentState(): LinkState {
    return this.state;
  }

  get currentConnId(): string | undefined {
    return this.connId;
  }

  /** True when the socket can accept frames right now. `host_offline` still
   * has a live socket — the device is asleep, the relay is not. */
  get isOpen(): boolean {
    return this.socket !== undefined && (this.state === "online" || this.state === "host_offline");
  }

  async start(): Promise<void> {
    this.userClosed = false;
    await this.dial();
  }

  /**
   * Resolve once the socket is actually open and the relay has accepted it.
   *
   * `start()` resolves as soon as the socket is *created*, which is not the same
   * thing: frames sent before the upgrade completes are silently dropped by the
   * transport. Callers that immediately issue RPC (a store registering its
   * watches, a UI probing `EngineInfo`) must await this instead.
   */
  waitForOnline(timeoutMs = 15_000): Promise<void> {
    if (this.state === "online" || this.state === "host_offline") return Promise.resolve();
    if (this.userClosed || this.state === "closed") {
      return Promise.reject(new Error("link closed"));
    }
    return new Promise<void>((resolve, reject) => {
      let settled = false;
      const finish = (error?: Error): void => {
        if (settled) return;
        settled = true;
        this.timers.clearTimeout(timer);
        this.stateListeners.delete(listener);
        if (error) reject(error);
        else resolve();
      };
      const listener = (state: LinkState, detail?: string): void => {
        if (state === "online" || state === "host_offline") finish();
        else if (state === "unauthorized") finish(new Error(detail ?? "unauthorized"));
        else if (state === "closed") finish(new Error(detail ?? "link closed"));
      };
      this.stateListeners.add(listener);
      const timer = this.timers.setTimeout(
        () => finish(new Error(`relay did not accept the connection within ${timeoutMs}ms`)),
        timeoutMs
      );
    });
  }

  /** Stop for good (sign-out, page teardown). */
  close(reason = "closed"): void {
    this.userClosed = true;
    this.teardown();
    this.setState("closed", reason);
  }

  /**
   * Send one RPC envelope. Returns false when the link is down, so the caller
   * can surface "offline" instead of silently queueing (the engine's command
   * ledger is the durable queue, not this socket).
   */
  sendRpc(text: string): boolean {
    const socket = this.socket;
    if (socket === undefined || !this.isOpen) return false;
    try {
      socket.send(encodeDeviceFrame({ s: RPC_KIND, k: RPC_KIND }, new TextEncoder().encode(text)));
      return true;
    } catch (error) {
      this.warn(`rpc send failed: ${String(error)}`);
      return false;
    }
  }

  /**
   * Called on page visibility changes and the UI's Retry button: redial now if
   * the link looks dead, otherwise send a liveness echo.
   */
  probeNow(): void {
    if (this.userClosed) return;
    if (this.socket === undefined) {
      this.scheduleReconnect(0);
      return;
    }
    const now = this.timers.now();
    if (this.echoSeen && now - this.lastEchoAt > this.echoDeadlineMs) {
      this.warn("host echo silent past deadline; redialing");
      this.dropSocket("host echo silent");
      return;
    }
    if (now - this.lastInboundAt > this.silenceDeadlineMs) {
      this.warn("relay silent past lease; redialing");
      this.dropSocket("relay silent");
      return;
    }
    this.sendEcho();
  }

  private async dial(): Promise<void> {
    if (this.userClosed) return;
    const generation = ++this.generation;
    this.setState(this.attempt === 0 ? "connecting" : "reconnecting");
    let token: string | undefined;
    try {
      token = await this.options.tokenProvider();
    } catch (error) {
      this.warn(`token provider failed: ${String(error)}`);
      token = undefined;
    }
    if (generation !== this.generation || this.userClosed) return;
    if (token === undefined || token === "") {
      this.setState("unauthorized", "no access token");
      return;
    }
    const connId = this.timers.uuid();
    const url = deviceRoomWsUrl({
      edgeUrl: this.options.edgeUrl,
      deviceId: this.options.deviceId,
      role: "client",
      connId,
      token
    });
    let socket: SocketLike;
    try {
      socket = this.socketFactory(url);
    } catch (error) {
      this.warn(`socket open failed: ${String(error)}`);
      this.scheduleReconnect();
      return;
    }
    this.connId = connId;
    this.socket = socket;
    this.echoSeen = false;
    // Without this Safari hands back a Blob and the frame codec has nothing to
    // read. `browserSocketFactory` sets it too; keep it here so any factory
    // (including a test fake) gets the same guarantee.
    socket.binaryType = "arraybuffer";
    socket.onopen = () => {
      if (generation !== this.generation) return;
      this.attempt = 0;
      this.lastEchoAt = this.timers.now();
      this.lastInboundAt = this.lastEchoAt;
      this.setState("online");
      // Send the first echo immediately: it seeds the cadence AND is the fast
      // feature-detection probe for a host that echoes.
      this.sendEcho();
      this.startEchoTimer(generation);
      this.options.onReady?.();
    };
    socket.onmessage = (event) => this.handleMessage(event.data, generation);
    socket.onclose = (event) => {
      if (generation !== this.generation) return;
      this.teardownSocket();
      if (this.userClosed) return;
      const code = event?.code ?? 0;
      if (code === 1008 || code === 1002) {
        // Policy violation / frame error: do not hammer the relay.
        this.setState("unauthorized", `closed by relay (${code})`);
        return;
      }
      this.scheduleReconnect();
    };
    socket.onerror = () => {
      if (generation !== this.generation) return;
      this.warn("socket error");
    };
  }

  private handleMessage(data: unknown, generation: number): void {
    if (generation !== this.generation) return;
    if (typeof data === "string") {
      // The DO's hibernation-safe auto-response answers the text "ping" with
      // "pong" without waking; either way it is not RPC.
      if (data === "pong") this.lastInboundAt = this.timers.now();
      return;
    }
    let bytes: Uint8Array;
    if (data instanceof ArrayBuffer) bytes = new Uint8Array(data);
    else if (ArrayBuffer.isView(data)) {
      bytes = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
    } else {
      this.warn("dropping non-binary socket message");
      return;
    }
    this.lastInboundAt = this.timers.now();
    let header;
    let payload;
    try {
      ({ header, payload } = decodeDeviceFrame(bytes));
    } catch (error) {
      // Malformed frames are the relay's or a buggy peer's problem; logging
      // beats tearing down a working session.
      this.warn(
        `dropping malformed device frame: ${
          error instanceof DeviceFrameError ? error.message : String(error)
        }`
      );
      return;
    }
    switch (header.k) {
      case RPC_KIND:
        // An RPC frame proves the whole path just as well as an echo.
        this.lastEchoAt = this.timers.now();
        this.echoSeen = true;
        this.options.onRpcText?.(new TextDecoder().decode(payload));
        return;
      case ECHO_KIND:
        this.lastEchoAt = this.timers.now();
        this.echoSeen = true;
        return;
      case RELAY_KIND: {
        const code = relayErrorCode(payload) ?? "relay error";
        if (code === HOST_OFFLINE || code === HOST_CLOSED) {
          // The workstation is asleep. Keep the socket (the relay is fine) and
          // keep probing: a host that rejoins needs no new client dial.
          this.setState("host_offline", code);
          return;
        }
        this.warn(`relay control: ${code}`);
        return;
      }
      case NUDGE_KIND:
        // Edge → host delivery hints. Nothing for a browser to do; the host
        // opens the doc and drains its own durable commands.
        return;
      default:
        this.warn(`ignoring unknown frame kind ${JSON.stringify(header.k)}`);
    }
  }

  private sendEcho(): void {
    const socket = this.socket;
    if (socket === undefined) return;
    try {
      socket.send(encodeDeviceFrame({ s: ECHO_KIND, k: ECHO_KIND }));
    } catch (error) {
      this.warn(`echo send failed: ${String(error)}`);
    }
  }

  private startEchoTimer(generation: number): void {
    if (this.echoTimer !== undefined) this.timers.clearInterval(this.echoTimer);
    this.echoTimer = this.timers.setInterval(() => {
      if (generation !== this.generation) return;
      const now = this.timers.now();
      if (this.echoSeen && now - this.lastEchoAt > this.echoDeadlineMs) {
        this.warn("host echo silent past deadline; link suspect");
        this.dropSocket("host echo silent");
        return;
      }
      if (now - this.lastInboundAt > this.silenceDeadlineMs) {
        this.warn("relay silent past lease; link suspect");
        this.dropSocket("relay silent");
        return;
      }
      this.sendEcho();
    }, this.echoIntervalMs);
  }

  private dropSocket(reason: string): void {
    this.teardownSocket();
    if (!this.userClosed) this.scheduleReconnect(0, reason);
  }

  private scheduleReconnect(delayMs?: number, detail?: string): void {
    if (this.userClosed || this.reconnectTimer !== undefined) return;
    const delay = delayMs ?? this.nextBackoff();
    this.setState("reconnecting", detail);
    this.reconnectTimer = this.timers.setTimeout(() => {
      this.reconnectTimer = undefined;
      void this.dial();
    }, delay);
  }

  private nextBackoff(): number {
    this.attempt += 1;
    const raw = Math.min(this.backoffMaxMs, this.backoffBaseMs * 2 ** (this.attempt - 1));
    // Jitter keeps a fleet of tabs from stampeding the relay after an outage.
    const jitter = 0.5 + this.timers.random() * 0.5;
    return Math.round(raw * jitter);
  }

  private teardownSocket(): void {
    if (this.echoTimer !== undefined) {
      this.timers.clearInterval(this.echoTimer);
      this.echoTimer = undefined;
    }
    const socket = this.socket;
    this.socket = undefined;
    if (socket !== undefined) {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onclose = null;
      socket.onerror = null;
      try {
        socket.close(1000, "client closing");
      } catch {
        /* already gone */
      }
    }
  }

  private teardown(): void {
    this.generation += 1;
    this.teardownSocket();
    if (this.reconnectTimer !== undefined) {
      this.timers.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
  }

  private setState(state: LinkState, detail?: string): void {
    if (this.state === state && detail === undefined) return;
    this.state = state;
    this.options.onState?.(state, detail);
    for (const listener of this.stateListeners) listener(state, detail);
  }

  private warn(message: string): void {
    this.options.onWarning?.(message);
  }
}
